/**
 * Modelul de navigație din anexa „Harta ecranelor". Fiecare element declară
 * permisiunea care îl face vizibil (FR-ADM-024) și faza în care este livrat.
 * Ecranele de fază 2 și 3 apar dezactivate, ca structura să fie vizibilă
 * din start, nu ca surpriză la livrare.
 */
export type Phase = 1 | 2 | 3

export interface NavItem {
  key: string
  label: string
  path: string
  permission: string
  phase: Phase
}

export interface NavGroup {
  key: string
  label: string
  icon: string
  items: NavItem[]
}

export const NAVIGATION: NavGroup[] = [
  {
    key: 'acasa', label: 'Acasă', icon: 'Dashboard',
    items: [
      { key: 'dashboard', label: 'Tablou de bord', path: '/', permission: 'rapoarte.operationale.vizualizare', phase: 1 },
      { key: 'aprobari', label: 'Aprobările mele', path: '/aprobari', permission: 'administrare.aprobari.vizualizare', phase: 1 },
      { key: 'notificarile-mele', label: 'Notificările mele', path: '/notificarile-mele', permission: 'administrare.notificarile-mele.vizualizare', phase: 1 },
      { key: 'alerte', label: 'Alerte', path: '/alerte', permission: 'rapoarte.operationale.vizualizare', phase: 1 },
    ],
  },
  {
    key: 'comercial', label: 'Comercial', icon: 'Handshake',
    items: [
      { key: 'cereri', label: 'Cereri client', path: '/comercial/cereri', permission: 'comercial.cereri.vizualizare', phase: 1 },
      { key: 'oferte', label: 'Oferte', path: '/comercial/oferte', permission: 'comercial.oferte.vizualizare', phase: 1 },
      { key: 'comenzi', label: 'Comenzi clienți', path: '/comercial/comenzi', permission: 'comercial.comenzi.vizualizare', phase: 1 },
      { key: 'clienti', label: 'Clienți', path: '/comercial/clienti', permission: 'comercial.clienti.vizualizare', phase: 1 },
      { key: 'preturi', label: 'Prețuri și discounturi', path: '/comercial/preturi', permission: 'comercial.preturi.vizualizare', phase: 1 },
      { key: 'simulare', label: 'Simulare de preț', path: '/comercial/simulare', permission: 'comercial.preturi.vizualizare', phase: 2 },
    ],
  },
  {
    key: 'achizitii', label: 'Achiziții', icon: 'ShoppingCart',
    items: [
      { key: 'cereri-ach', label: 'Cereri de achiziție', path: '/achizitii/cereri', permission: 'achizitii.cereri.vizualizare', phase: 1 },
      { key: 'contracte', label: 'Contracte furnizori', path: '/achizitii/contracte', permission: 'achizitii.contracte.vizualizare', phase: 1 },
      { key: 'comenzi-furn', label: 'Comenzi furnizor', path: '/achizitii/comenzi', permission: 'achizitii.comenzi.vizualizare', phase: 1 },
      { key: 'receptii', label: 'Recepții', path: '/achizitii/receptii', permission: 'achizitii.receptii.vizualizare', phase: 1 },
      { key: 'facturi-furn', label: 'Facturi furnizori', path: '/achizitii/facturi', permission: 'achizitii.facturi.vizualizare', phase: 1 },
      { key: 'furnizori', label: 'Furnizori', path: '/achizitii/furnizori', permission: 'achizitii.furnizori.vizualizare', phase: 1 },
    ],
  },
  {
    key: 'productie', label: 'Producție', icon: 'PrecisionManufacturing',
    items: [
      { key: 'plan', label: 'Plan de producție', path: '/productie/plan', permission: 'productie.plan.vizualizare', phase: 1 },
      { key: 'ordine', label: 'Ordine de producție', path: '/productie/ordine', permission: 'productie.ordine.vizualizare', phase: 1 },
      { key: 'rute', label: 'Rute de producție', path: '/productie/rute', permission: 'productie.rute.vizualizare', phase: 1 },
      { key: 'retetar', label: 'Rețetar (BOM)', path: '/productie/retetar', permission: 'productie.retetar.vizualizare', phase: 1 },
      { key: 'calitate', label: 'Control calitate', path: '/productie/calitate', permission: 'productie.calitate.vizualizare', phase: 1 },
      { key: 'shopfloor', label: 'Shopfloor', path: '/shopfloor', permission: 'productie.shopfloor.vizualizare', phase: 1 },
    ],
  },
  {
    key: 'stocuri', label: 'Stocuri', icon: 'Inventory',
    items: [
      { key: 'stoc', label: 'Stoc curent', path: '/stocuri/stoc', permission: 'stocuri.stoc.vizualizare', phase: 1 },
      { key: 'miscari', label: 'Mișcări de stoc', path: '/stocuri/miscari', permission: 'stocuri.miscari.vizualizare', phase: 1 },
      { key: 'loturi', label: 'Loturi și trasabilitate', path: '/stocuri/loturi', permission: 'stocuri.loturi.vizualizare', phase: 1 },
      { key: 'transferuri', label: 'Transferuri', path: '/stocuri/transferuri', permission: 'stocuri.transferuri.vizualizare', phase: 1 },
      { key: 'inventar', label: 'Inventar', path: '/stocuri/inventar', permission: 'stocuri.inventar.vizualizare', phase: 1 },
    ],
  },
  {
    key: 'logistica', label: 'Logistică', icon: 'LocalShipping',
    items: [
      { key: 'picking', label: 'Picking și packing', path: '/logistica/picking', permission: 'logistica.picking.vizualizare', phase: 1 },
      { key: 'expeditii', label: 'Expediții', path: '/logistica/expeditii', permission: 'logistica.expeditii.vizualizare', phase: 1 },
      { key: 'transportatori', label: 'Transportatori și rute', path: '/logistica/transportatori', permission: 'logistica.transportatori.vizualizare', phase: 2 },
    ],
  },
  {
    key: 'financiar', label: 'Financiar', icon: 'AccountBalance',
    items: [
      { key: 'facturi-emise', label: 'Facturi emise', path: '/financiar/facturi', permission: 'financiar.facturi-emise.vizualizare', phase: 1 },
      { key: 'incasari', label: 'Încasări și plăți', path: '/financiar/incasari-plati', permission: 'financiar.incasari-plati.vizualizare', phase: 1 },
      { key: 'reconciliere', label: 'Reconciliere bancară', path: '/financiar/reconciliere', permission: 'financiar.reconciliere.vizualizare', phase: 1 },
      { key: 'cashflow', label: 'Cash-flow', path: '/financiar/cashflow', permission: 'financiar.cashflow.vizualizare', phase: 1 },
    ],
  },
  {
    key: 'cheltuieli', label: 'Cheltuieli', icon: 'Receipt',
    items: [
      { key: 'chelt-proprii', label: 'Cheltuielile mele', path: '/cheltuieli/proprii', permission: 'cheltuieli.proprii.vizualizare', phase: 1 },
      { key: 'avansuri', label: 'Avansurile mele', path: '/cheltuieli/avansuri', permission: 'cheltuieli.proprii.vizualizare', phase: 1 },
      { key: 'deconturi-mele', label: 'Deconturile mele', path: '/cheltuieli/deconturi', permission: 'cheltuieli.proprii.vizualizare', phase: 1 },
      { key: 'deconturi-toate', label: 'Deconturi (toate)', path: '/cheltuieli/toate', permission: 'cheltuieli.toate.vizualizare', phase: 1 },
    ],
  },
  {
    key: 'active', label: 'Active fixe', icon: 'Business',
    items: [
      { key: 'registru', label: 'Registru active', path: '/active/registru', permission: 'active.registru.vizualizare', phase: 1 },
      { key: 'intrari', label: 'Intrări', path: '/active/intrari', permission: 'active.intrari.vizualizare', phase: 1 },
      { key: 'iesiri', label: 'Ieșiri', path: '/active/iesiri', permission: 'active.iesiri.vizualizare', phase: 1 },
      { key: 'amortizare', label: 'Amortizare', path: '/active/amortizare', permission: 'active.amortizare.vizualizare', phase: 1 },
    ],
  },
  {
    key: 'mentenanta', label: 'Mentenanță', icon: 'Build',
    items: [
      { key: 'echipamente', label: 'Echipamente și matrițe', path: '/mentenanta/echipamente', permission: 'mentenanta.echipamente.vizualizare', phase: 1 },
      { key: 'calendar-mnt', label: 'Calendar mentenanță', path: '/mentenanta/calendar', permission: 'mentenanta.calendar.vizualizare', phase: 1 },
      { key: 'ordine-lucru', label: 'Ordine de lucru', path: '/mentenanta/ordine-lucru', permission: 'mentenanta.ordine-lucru.vizualizare', phase: 1 },
      { key: 'piese', label: 'Piese de schimb', path: '/mentenanta/piese', permission: 'mentenanta.piese.vizualizare', phase: 2 },
    ],
  },
  {
    key: 'planificare', label: 'Planificare', icon: 'Insights',
    items: [
      { key: 'buget', label: 'Buget', path: '/planificare/buget', permission: 'planificare.buget.vizualizare', phase: 2 },
      { key: 'kpi', label: 'KPI și P&L', path: '/planificare/kpi', permission: 'planificare.kpi.vizualizare', phase: 1 },
    ],
  },
  {
    key: 'rapoarte', label: 'Rapoarte', icon: 'Assessment',
    items: [
      { key: 'catalog', label: 'Catalog rapoarte', path: '/rapoarte', permission: 'rapoarte.operationale.vizualizare', phase: 1 },
      { key: 'rec-saga', label: 'Reconciliere cu SAGA', path: '/rapoarte/saga', permission: 'rapoarte.reconciliere-saga.vizualizare', phase: 1 },
    ],
  },
  {
    key: 'configurare', label: 'Configurare', icon: 'Tune',
    items: [
      { key: 'fluxuri-aprobare', label: 'Fluxuri de aprobare', path: '/administrare/fluxuri-aprobare', permission: 'administrare.fluxuri-aprobare.vizualizare', phase: 1 },
      { key: 'reguli-notificare', label: 'Reguli de notificare', path: '/administrare/reguli-notificare', permission: 'administrare.notificari.vizualizare', phase: 1 },
      { key: 'puncte-lucru', label: 'Puncte de lucru', path: '/administrare/puncte-lucru', permission: 'administrare.puncte-lucru.vizualizare', phase: 1 },
      { key: 'taxe', label: 'Taxe', path: '/financiar/taxe', permission: 'financiar.taxe.vizualizare', phase: 1 },
      { key: 'parametri', label: 'Profil firmă', path: '/administrare/parametri', permission: 'administrare.parametri.vizualizare', phase: 1 },
      { key: 'um', label: 'UM și conversii', path: '/nomenclatoare/um', permission: 'nomenclatoare.um.vizualizare', phase: 1 },
      { key: 'depozite', label: 'Depozite și locații', path: '/stocuri/depozite', permission: 'stocuri.depozite.vizualizare', phase: 1 },
    ],
  },
  {
    key: 'nomenclatoare', label: 'Nomenclatoare', icon: 'MenuBook',
    items: [
      { key: 'articole', label: 'Articole', path: '/nomenclatoare/articole', permission: 'nomenclatoare.articole.vizualizare', phase: 1 },
      { key: 'seturi', label: 'Seturi', path: '/nomenclatoare/seturi', permission: 'nomenclatoare.seturi.vizualizare', phase: 1 },
    ],
  },
  {
    key: 'administrare', label: 'Administrare', icon: 'Settings',
    items: [
      { key: 'utilizatori', label: 'Utilizatori', path: '/administrare/utilizatori', permission: 'administrare.utilizatori.vizualizare', phase: 1 },
      { key: 'roluri', label: 'Roluri și permisiuni', path: '/administrare/roluri', permission: 'administrare.roluri.vizualizare', phase: 1 },
      { key: 'sesiuni', label: 'Sesiuni active', path: '/administrare/sesiuni', permission: 'administrare.utilizatori.vizualizare', phase: 1 },
      { key: 'saga', label: 'Integrare SAGA', path: '/administrare/saga', permission: 'administrare.integrare-saga.vizualizare', phase: 1 },
      { key: 'audit', label: 'Audit trail', path: '/administrare/audit', permission: 'administrare.audit.vizualizare', phase: 1 },
    ],
  },
]

/** Găsește elementul de meniu pentru o rută, pentru titlu și breadcrumb. */
export function findNavItem(pathname: string): { group: NavGroup; item: NavItem } | null {
  for (const group of NAVIGATION) {
    const item = group.items.find((i) => i.path === pathname)
    if (item) return { group, item }
  }
  return null
}
